import React, { useState, useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import DiscoverModal from './DiscoverModal';

const CARDS = [
  { id: 'journal', title: 'Journal tool', action: 'I want to journal', tiltImage: '/image/intro18.png', baseImage: '/image/intro14.png' },
  { id: 'podcast', title: 'Podcast tool', action: 'Create my personalized podcast', tiltImage: '/image/intro15.png', baseImage: '/image/guy11.jpeg' },
  { id: 'study', title: 'Study tool', action: 'Create flashcards', tiltImage: '/image/intro4.png', baseImage: '/image/intro13.png' },
  { id: 'planner', title: 'Schedule tool', action: 'Set up my planner', tiltImage: '/image/intro2.png', baseImage: '/image/intro14.png' },
];

const TOOL_NAMES = {
  journal: 'Journal Tool',
  podcast: 'Podcast Tool',
  study: 'Study Tool',
  planner: 'Smart Planner',
};

const HorizontalAndFeaturedCards = ({ onCardClick, availableTools = [], onEnableTool = null, onNavigateToTool = null }) => {
  const [activeTab, setActiveTab] = useState('forYou');
  const [loadedImages, setLoadedImages] = useState({});
  const [showDiscover, setShowDiscover] = useState(false);
  const [enableModal, setEnableModal] = useState(null);
  const [isEnabling, setIsEnabling] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [cardImages, setCardImages] = useState({});
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const scrollRef = useRef(null);

  // ✅ RESPONSIVE DETECTION
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ✅ PRE-LOAD IMAGES FOR SKELETON
  useEffect(() => {
    const images = {};
    CARDS.forEach(card => {
      const img = new Image();
      img.src = card.tiltImage;
      img.onload = () => {
        images[card.id] = card.tiltImage;
        setCardImages(prev => ({ ...prev, [card.id]: card.tiltImage }));
      };
    });
  }, []);

  // ✅ INITIAL LOADING EFFECT
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  // Stagger load images
  useEffect(() => {
    if (!isLoading) {
      [0, 1, 2, 3].sort(() => Math.random() - 0.5).forEach((idx, i) => {
        setTimeout(() => setLoadedImages(p => ({ ...p, [CARDS[idx].id]: true })), i * 150);
      });
    }
  }, [isLoading]);

  const isToolEnabled = (toolId) => availableTools.find(t => t.id === toolId)?.visible === true;

  const handleCardClick = (card) => {
    if (!isToolEnabled(card.id)) {
      setEnableModal(card);
    } else if (onNavigateToTool) {
      onNavigateToTool(card.id);
    }
  };

  const handleEnableTool = async () => {
    if (!enableModal?.id || !onEnableTool) return;
    setIsEnabling(true);
    try {
      await onEnableTool(enableModal.id);
      await new Promise(r => setTimeout(r, 800));
      onNavigateToTool?.(enableModal.id);
      setEnableModal(null);
    } catch (error) {
      console.error('Error enabling tool:', error);
      setIsEnabling(false);
    }
  };

  const scroll = (dir) => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: (dir === 'left' ? -200 : 200), behavior: 'smooth' });
    }
  };

  // ✅ LOADING SKELETON - HORIZONTAL FOR ALL SIZES
  if (isLoading) {
    return (
      <div style={{ 
        width: '100%', 
        maxWidth: isMobile ? '100%' : '1200px', 
        margin: '0 auto', 
        padding: isMobile ? '20px' : '30px 60px' 
      }}>
        {/* TABS SKELETON */}
        <div style={{ display: 'flex', gap: '20px', marginBottom: '24px', paddingLeft: '4px' }}>
          <div className="blur-tab" style={{ width: '60px', height: '16px', borderRadius: '4px' }}></div>
          <div className="blur-tab" style={{ width: '60px', height: '16px', borderRadius: '4px' }}></div>
        </div>

        {/* SCROLL CONTAINER SKELETON */}
        <div style={{ position: 'relative', marginBottom: '48px' }}>
          <div
            style={{
              display: 'flex',
              gap: '20px',
              paddingLeft: isMobile ? '32px' : '0',
              paddingRight: isMobile ? '60px' : '0',
              marginLeft: isMobile ? '-70px' : '0',
              marginRight: isMobile ? '-62px' : '0',
              justifyContent: 'flex-start',
            }}
          >
            {CARDS.map(card => (
              <div key={card.id} style={{ 
                flexShrink: 0, 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                gap: '16px',
                width: 'auto'
              }}>
                {/* CARD SKELETON */}
                <div style={{ width: '100px', height: '100px', position: 'relative', perspective: '1200px' }}>
                  <div 
                    className="blur-image-skeleton"
                    style={{ 
                      position: 'absolute',
                      top: '11px',
                      left: '0',
                      width: '80px', 
                      height: '80px', 
                      borderRadius: '12px',
                      backgroundImage: cardImages[card.id] ? `url(${cardImages[card.id]})` : 'none',
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      transform: 'rotateX(10deg) rotateY(-3deg) rotateZ(-3deg)',
                      transformStyle: 'preserve-3d',
                    }}
                  ></div>
                </div>

                <div className="blur-tab" style={{ width: '85px', height: '17px', borderRadius: '4px' }}></div>
                <div className="blur-tab" style={{ width: '65px', height: '32px', borderRadius: '16px' }}></div>
              </div>
            ))}
          </div>
        </div>

        <style jsx>{`
          .blur-image-skeleton {
            filter: blur(6px);
            animation: blurPulse 0.8s ease-in-out infinite;
          }

          .blur-tab {
            background: rgba(0, 0, 0, 0.08);
            animation: blurPulse 0.8s ease-in-out infinite;
            filter: blur(3px);
          }

          @keyframes blurPulse {
            0%, 100% { opacity: 0.4; }
            50% { opacity: 0.7; }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div style={{ 
      width: '100%', 
      maxWidth: isMobile ? '100%' : '1200px', 
      margin: '0 auto', 
      padding: isMobile ? '20px' : '30px 60px' 
    }}>
      {/* TABS */}
      <div style={{ display: 'flex', gap: '20px', marginBottom: '24px', paddingLeft: '4px' }}>
        {['forYou', 'learn'].map(tab => (
          <button
            key={tab}
            onClick={() => tab === 'forYou' ? setActiveTab('forYou') : setShowDiscover(true)}
            style={{
              padding: '8px 4px',
              fontSize: '13px',
              fontWeight: '500',
              background: 'none',
              border: 'none',
              color: activeTab === 'forYou' && tab === 'forYou' ? '#333' : 'rgba(60, 61, 61, 0.5)',
              cursor: 'pointer',
              position: 'relative',
              transition: 'color 0.3s',
            }}
          >
            {tab === 'forYou' ? 'For you' : 'Learn more'}
            {activeTab === 'forYou' && tab === 'forYou' && (
              <div style={{ position: 'absolute', bottom: '-2px', left: 0, right: 0, height: '1px', background: '#313131' }} />
            )}
          </button>
        ))}
      </div>

      {/* CARDS CONTAINER - HORIZONTAL SCROLL FOR ALL SIZES */}
      <div style={{ position: 'relative', marginBottom: '48px' }}>
        {/* SCROLL ARROWS - ALL SIZES */}
        {['left', 'right'].map(dir => (
          <button
            key={dir}
            onClick={() => scroll(dir)}
            className="scroll-arrow"
            style={{
              position: 'absolute',
              [dir]: '-12px',
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 10,
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(255, 255, 255, 0.38)',
              border: '1px solid rgba(0, 0, 0, 0.1)',
              borderRadius: '50%',
              cursor: 'pointer',
              opacity: 0,
              transition: 'opacity 0.3s',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="black" strokeWidth="2">
              <path d={dir === 'left' ? 'M15 18l-6-6 6-6' : 'M9 18l6-6-6-6'} />
            </svg>
          </button>
        ))}

        {/* CARDS HORIZONTAL SCROLL */}
        <div
          ref={scrollRef}
          style={{
            display: 'flex',
            gap: '20px',
            overflowX: 'auto',
            paddingLeft: isMobile ? '32px' : '0',
            paddingRight: isMobile ? '60px' : '0',
            marginLeft: isMobile ? '-70px' : '0',
            marginRight: isMobile ? '-62px' : '0',
            paddingBottom: '8px',
            scrollbarWidth: 'none',
            justifyContent: 'flex-start',
          }}
          onMouseEnter={(e => e.currentTarget.parentElement.querySelectorAll('.scroll-arrow').forEach(a => a.style.opacity = '1'))}
          onMouseLeave={(e => e.currentTarget.parentElement.querySelectorAll('.scroll-arrow').forEach(a => a.style.opacity = '0'))}
        >
          {CARDS.map(card => {
            const isLoaded = loadedImages[card.id];
            const isEnabled = isToolEnabled(card.id);

            return (
              <div key={card.id} style={{ 
                flexShrink: 0, 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                gap: '16px',
                width: 'auto'
              }}>
                {/* CARD */}
                <div className="card-container" style={{ width: '100px', height: '100px', position: 'relative', perspective: '1200px' }}>
                  {/* BASE IMAGE */}
                  <div className="card-image-base"
                    style={{
                      position: 'absolute',
                      bottom: '6px',
                      right: '6px',
                      width: '80px',
                      height: '80px',
                      borderRadius: '12px',
                      overflow: 'hidden',
                      zIndex: 1,
                      background: 'rgba(255, 255, 255, 0.15)',
                      backdropFilter: 'blur(10px)',
                      boxShadow: '0 4px 4px rgba(0, 0, 0, 0.28)',
                      opacity: isLoaded ? 1 : 0,
                      filter: isLoaded ? 'blur(0)' : 'blur(10px)',
                      transform: isLoaded ? 'scale(1)' : 'scale(0.9)',
                      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                  >
                    <img src={card.baseImage} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => e.target.style.display = 'none'} />
                  </div>

                  {/* TILT IMAGE */}
                  <div className="card-image"
                    style={{
                      position: 'absolute',
                      top: '11px',
                      left: '0',
                      width: '80px',
                      height: '80px',
                      borderRadius: '12px',
                      overflow: 'hidden',
                      zIndex: 2,
                      background: 'rgba(255, 255, 255, 0.15)',
                      backdropFilter: 'blur(10px)',
                      border: '2px solid rgba(185, 185, 185, 0.06)',
                      cursor: 'pointer',
                      transform: isLoaded ? 'rotateX(10deg) rotateY(-3deg) rotateZ(-3deg)' : 'rotateX(10deg) rotateY(-3deg) rotateZ(-3deg) scale(0.9)',
                      transformStyle: 'preserve-3d',
                      opacity: isLoaded ? 1 : 0,
                      filter: isLoaded ? 'blur(0)' : 'blur(10px)',
                      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                  >
                    <img src={card.tiltImage} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => e.target.style.display = 'none'} />
                    {!isEnabled && (
                      <div style={{ position: 'absolute', inset: 0, background: 'rgba(0, 0, 0, 0.4)', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                          <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                        </svg>
                      </div>
                    )}
                  </div>
                </div>

                {/* TITLE */}
                <h3 className="card-title" style={{ fontSize: '14.5px', fontWeight: '400', color: '#171717ab', margin: 0, textAlign: 'center', lineHeight: '1.2' }}>
                  {card.title}
                </h3>

                {/* BUTTON */}
                <button className="card-button"
                  onClick={() => handleCardClick(card)}
                  style={{
                    padding: '5px 24px',
                    background: isEnabled ? '#e4e4e49a' : '#1d1d1dff',
                    border: isEnabled ? 'none' : '1px solid #e5e7eb33',
                    borderRadius: '16px',
                    color: isEnabled ? 'black' : '#fff',
                    fontSize: '12.5px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    boxShadow: isEnabled ? '0 2px 2px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.78)' : 'none',
                  }}
                >
                  {isEnabled ? 'Try now' : 'Enable'}
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* MODAL */}
      {enableModal && (
        <>
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0, 0, 0, 0.5)', zIndex: 1000, backdropFilter: 'blur(4px)' }} onClick={() => !isEnabling && setEnableModal(null)} />
          <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 1001, background: 'white', borderRadius: '24px', padding: '32px', maxWidth: '380px', width: '90%', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.15)', animation: 'modalFadeIn 0.3s ease-out' }}>
            <button onClick={() => !isEnabling && setEnableModal(null)} disabled={isEnabling} style={{ position: 'absolute', top: '16px', right: '16px', background: 'none', border: 'none', cursor: isEnabling ? 'not-allowed' : 'pointer', padding: '8px', color: '#6b7280', opacity: isEnabling ? 0.5 : 1, fontSize: '20px' }}>
              <X size={20} />
            </button>
            <div style={{ textAlign: 'center' }}>
              <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#1f2937', margin: '0 0 12px 0' }}>
                Enable {TOOL_NAMES[enableModal.id]}?
              </h2>
              <p style={{ fontSize: '14px', color: '#6b7280', margin: '0 0 28px 0', lineHeight: '1.6' }}>
                You need to enable this tool first to start using it. It will be added to your toolkit.
              </p>
              <div style={{ display: 'flex', gap: '12px', flexDirection: 'column' }}>
                <button onClick={handleEnableTool} disabled={isEnabling} style={{ padding: '10px 24px', background: isEnabling ? '#9ca3af' : '#000', color: 'white', border: 'none', borderRadius: '28px', fontSize: '14px', fontWeight: '500', cursor: isEnabling ? 'not-allowed' : 'pointer', minHeight: '44px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                  {isEnabling ? (
                    <>
                      <div style={{ width: '14px', height: '14px', border: '2px solid rgba(255,255,255,0.3)', borderTopColor: 'white', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                      <span>Enabling...</span>
                    </>
                  ) : (
                    'Enable tool'
                  )}
                </button>
                <button onClick={() => setEnableModal(null)} disabled={isEnabling} style={{ padding: '11px 24px', background: '#f3f4f6', color: '#6b7280', border: '1px solid #e5e7eb', borderRadius: '28px', fontSize: '14px', fontWeight: '500', cursor: isEnabling ? 'not-allowed' : 'pointer', opacity: isEnabling ? 0.5 : 1 }}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
          <style>{`@keyframes modalFadeIn { from { opacity: 0; transform: translate(-50%, -50%) scale(0.95); } to { opacity: 1; transform: translate(-50%, -50%) scale(1); } } @keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </>
      )}

      {/* DISCOVER MODAL */}
      <DiscoverModal isOpen={showDiscover} onClose={() => setShowDiscover(false)} />

      <style>{`
        div::-webkit-scrollbar { display: none; }
        
        @media (max-width: 768px) {
          .card-title {
            font-size: 13px !important;
          }
          .card-button {
            font-size: 12px !important;
          }
          .card-container {
            width: 85px !important;
            height: 85px !important;
          }
          .card-image {
            width: 68px !important;
            height: 68px !important;
            top: 10px !important;
          }
          .card-image-base {
            width: 68px !important;
            height: 68px !important;
            bottom: 5px !important;
            right: 5px !important;
          }
        }
      `}</style>
    </div>
  );
};

export default HorizontalAndFeaturedCards;